import { heapAddEventProperties, heapAddUserProperties } from '@m/utils/heap';

/**
 * Shared attribution + breakdown props for reservation Funnel reporting in Heap.
 * See Reservations Reporting requirements → Funnel (Heap FE only).
 */

const ATTRIBUTION_STORAGE_KEY = 'reservation_funnel_attribution';

export type TrafficSource =
  'paid' | 'organic' | 'email' | 'sms' | 'in-app' | 'direct' | 'referral' | 'unknown';

export type AuthStatus = 'logged_in' | 'guest';

export type MemberStatus = 'new' | 'returning' | 'guest' | 'unknown';

type StoredAttribution = {
  /** Marks that the first-touch snapshot for this session was already captured. */
  captured?: boolean;
  gclid?: string;
  /** Hostname of the first page (e.g. `go.metropolis.io`); `landingPage` is path-only. */
  landingDomain?: string;
  landingPage?: string;
  /** `document.referrer` at first touch; later pages see a first-party referrer instead. */
  referrer?: string;
  /** Where the session really began when it arrived from another Metropolis site (`start_domain`). */
  startDomain?: string;
  /** Path on `startDomain` (`start_page`). */
  startPage?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_source?: string;
  utm_term?: string;
};

/** Attribution params carried on links from the marketing site into the customer app. */
const FORWARDED_ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
] as const;

export type ReservationFunnelContext = {
  authStatus: AuthStatus;
  client: 'web';
  landingDomain: string;
  landingPage: string;
  memberStatus: MemberStatus;
  /** Site the session began on; differs from `landingDomain` when a link brought the user across. */
  sessionStartDomain: string;
  sessionStartPage: string;
  trafficSource: TrafficSource;
  utm_campaign: string;
  utm_content: string;
  utm_medium: string;
  utm_source: string;
  utm_term: string;
};

type IdentityOpts = {
  authenticated?: boolean;
  userAddedAt?: number | null;
};

/** Last identity synced via `syncReservationFunnelEventProperties` — used when trackers omit auth opts. */
let syncedIdentity: IdentityOpts | undefined;

function readStoredAttribution(): StoredAttribution {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as StoredAttribution;
  } catch {
    return {};
  }
}

function writeStoredAttribution(value: StoredAttribution) {
  try {
    sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore quota / private-mode failures; event props still work for the current page.
  }
}

function isFirstPartyHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return (
    host === 'metropolis.io' ||
    host.endsWith('.metropolis.io') ||
    host === 'mpolis.io' ||
    host.endsWith('.mpolis.io')
  );
}

const SESSION_START_HOST = /(^|\.)(metropolis\.io|mpolis\.io|metrop\.io)$|^localhost$/;
const MAX_SESSION_START_PAGE_LENGTH = 200;

/**
 * Reads the `start_domain` / `start_page` that the marketing site puts on links into the app.
 * They arrive in a user-editable URL, so anything that is not a Metropolis hostname plus a
 * local path is dropped rather than copied into Heap.
 */
function readSessionStart(
  params: URLSearchParams,
): Pick<StoredAttribution, 'startDomain' | 'startPage'> {
  const startDomain = (params.get('start_domain') || '').toLowerCase();
  const startPage = params.get('start_page') || '';
  const valid =
    SESSION_START_HOST.test(startDomain) &&
    startPage.startsWith('/') &&
    !startPage.startsWith('//') &&
    startPage.length <= MAX_SESSION_START_PAGE_LENGTH;
  return valid ? { startDomain, startPage } : {};
}

/**
 * Search engines whose referrer means an unpaid search visit. Anchored so that other hosts on the
 * same brand (`mail.google.com`, `docs.google.com`) stay `referral`. Paid search is caught earlier
 * by `gclid` / `utm_medium`, so a bare search referrer here is organic.
 */
const COUNTRY_TLD = '(?:com|(?:co|com)\\.[a-z]{2}|[a-z]{2})'; // com, co.uk, com.au, de
const SEARCH_ENGINE_HOST = new RegExp(
  `^(www\\.)?(google\\.${COUNTRY_TLD}|yandex\\.${COUNTRY_TLD}|bing\\.com|duckduckgo\\.com|ecosia\\.org|baidu\\.com|startpage\\.com)$|^search\\.(yahoo|brave)\\.com$`,
);

function classifyTrafficSource(attrs: StoredAttribution): TrafficSource {
  const medium = (attrs.utm_medium || '').toLowerCase();
  const source = (attrs.utm_source || '').toLowerCase();

  if (attrs.gclid || /cpc|ppc|paid|paidsearch|display|retarget/.test(medium)) {
    return 'paid';
  }
  if (medium === 'email' || source === 'email' || source.includes('iterable')) {
    return 'email';
  }
  if (medium === 'sms' || source === 'sms' || source.includes('twilio')) {
    return 'sms';
  }
  if (
    medium === 'push' ||
    medium === 'in-app' ||
    medium === 'in_app' ||
    source === 'app' ||
    source === 'in-app'
  ) {
    return 'in-app';
  }
  if (medium === 'organic' || source === 'organic') {
    return 'organic';
  }
  if (attrs.utm_source || attrs.utm_medium) {
    return 'unknown';
  }
  // Sessions captured before `referrer` was stored fall back to the live referrer.
  const referrer = attrs.referrer ?? (typeof document !== 'undefined' ? document.referrer : '');
  if (referrer) {
    try {
      const refHost = new URL(referrer).hostname.toLowerCase();
      if (SEARCH_ENGINE_HOST.test(refHost)) {
        return 'organic';
      }
      if (refHost && !isFirstPartyHost(refHost)) {
        return 'referral';
      }
    } catch {
      return 'referral';
    }
  }
  return 'direct';
}

/**
 * Capture first-touch UTMs / landing path for the session (does not overwrite existing).
 * Once a snapshot is captured, later navigations cannot fill previously absent fields.
 */
export function captureReservationAttributionFromUrl(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
  pathname: string = typeof window !== 'undefined' ? window.location.pathname : '',
) {
  const existing = readStoredAttribution();
  if (existing.captured) return;

  // Sessions captured before the `captured` marker still lock on first touch —
  // do not let a later URL fill previously absent UTM fields.
  if (existing.landingPage) {
    writeStoredAttribution({ ...existing, captured: true });
    return;
  }

  const params = new URLSearchParams(search);
  const next: StoredAttribution = {
    captured: true,
    landingDomain: typeof window !== 'undefined' ? window.location.hostname : '',
    landingPage: pathname || '/',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    ...readSessionStart(params),
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_content: params.get('utm_content') || undefined,
    utm_term: params.get('utm_term') || undefined,
    gclid: params.get('gclid') || undefined,
  };

  writeStoredAttribution(next);
}

export function getMemberStatus(opts?: {
  authenticated?: boolean;
  /** User.addedAt epoch ms — used as a coarse new vs returning proxy. */
  userAddedAt?: number | null;
}): MemberStatus {
  if (!opts?.authenticated) return 'guest';
  if (opts.userAddedAt == null || !Number.isFinite(opts.userAddedAt)) return 'unknown';
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  return Date.now() - opts.userAddedAt < sevenDaysMs ? 'new' : 'returning';
}

export function getReservationFunnelContext(opts?: {
  authenticated?: boolean;
  userAddedAt?: number | null;
}): ReservationFunnelContext {
  const stored = readStoredAttribution();
  // Prefer explicit opts; otherwise reuse the last synced identity so later funnel
  // events do not reclassify signed-in users as guests.
  const identity = opts ?? syncedIdentity;
  const authStatus: AuthStatus = identity?.authenticated ? 'logged_in' : 'guest';
  // Sessions captured before `landingDomain` was stored were all on the current host.
  const landingDomain =
    stored.landingDomain || (typeof window !== 'undefined' ? window.location.hostname : '');
  const landingPage =
    stored.landingPage || (typeof window !== 'undefined' ? window.location.pathname : '/');

  return {
    authStatus,
    client: 'web',
    landingDomain,
    landingPage,
    memberStatus: getMemberStatus(identity),
    sessionStartDomain: stored.startDomain || landingDomain,
    sessionStartPage: stored.startPage || landingPage,
    trafficSource: classifyTrafficSource(stored),
    utm_campaign: stored.utm_campaign || '',
    utm_content: stored.utm_content || '',
    utm_medium: stored.utm_medium || '',
    utm_source: stored.utm_source || '',
    utm_term: stored.utm_term || '',
  };
}

/**
 * Sticky Heap event + user props so later funnel steps inherit breakdown dimensions.
 */
export function syncReservationFunnelEventProperties(opts?: {
  authenticated?: boolean;
  userAddedAt?: number | null;
}) {
  captureReservationAttributionFromUrl();
  if (opts) {
    syncedIdentity = opts;
  }
  const ctx = getReservationFunnelContext(opts ?? syncedIdentity);
  heapAddEventProperties(ctx);
  heapAddUserProperties({
    reservationAuthStatus: ctx.authStatus,
    reservationMemberStatus: ctx.memberStatus,
    reservationTrafficSource: ctx.trafficSource,
    reservationLandingPage: ctx.landingPage,
  });
  return ctx;
}

/**
 * Attribution-only Heap event props, for surfaces that have no auth context (the marketing site).
 * Unlike `syncReservationFunnelEventProperties` this leaves `authStatus` / `memberStatus` and the
 * user props alone, so it cannot overwrite what the customer app already set for a signed-in user.
 * It does overwrite `landingDomain` / `landingPage` / `sessionStart*` / `trafficSource` / `utm_*`,
 * which Heap persists across subdomains and would otherwise carry stale customer-app values onto
 * marketing events.
 *
 * On go.metropolis.io the same props are also queued from `attributionEarlyScript` inside the Heap
 * snippet, before heap.js snapshots them onto the automatic pageview. This function still runs
 * after hydration and covers client navigations plus any load where that snippet did not.
 */
export function syncAttributionEventProperties() {
  captureReservationAttributionFromUrl();
  const {
    landingDomain,
    landingPage,
    sessionStartDomain,
    sessionStartPage,
    trafficSource,
    utm_campaign,
    utm_content,
    utm_medium,
    utm_source,
    utm_term,
  } = getReservationFunnelContext();
  heapAddEventProperties({
    landingDomain,
    landingPage,
    sessionStartDomain,
    sessionStartPage,
    trafficSource,
    utm_campaign,
    utm_content,
    utm_medium,
    utm_source,
    utm_term,
  });
}

/**
 * Adds this session's captured UTMs / gclid, and where the session started (`start_domain` /
 * `start_page`), to `href` so the customer app's own first-touch capture sees them (sessionStorage
 * is per-origin, so it cannot read what the marketing site stored).
 * Params already on the URL win. Returns `href` unchanged when there is nothing to add.
 */
export function withAttributionParams(href: string): string {
  const stored = readStoredAttribution();
  try {
    const url = new URL(href, window.location.href);
    let changed = false;
    const setIfAbsent = (key: string, value: string | undefined) => {
      if (value && !url.searchParams.has(key)) {
        url.searchParams.set(key, value);
        changed = true;
      }
    };
    for (const key of FORWARDED_ATTRIBUTION_KEYS) {
      setIfAbsent(key, stored[key]);
    }
    // A session that itself arrived from elsewhere passes its original start on, not this page.
    // The pair is only meaningful together, so nothing is sent before a page has been captured.
    const startPage = stored.startPage || stored.landingPage;
    if (startPage) {
      // Same pre-`landingDomain` fallback as `getReservationFunnelContext`: the current host.
      setIfAbsent(
        'start_domain',
        stored.startDomain || stored.landingDomain || window.location.hostname,
      );
      setIfAbsent('start_page', startPage);
    }
    return changed ? url.toString() : href;
  } catch {
    return href;
  }
}
